import {
  Sun,
  Phone,
  Mail,
  MapPin,
  X,
  InstagramIcon,
  MessageCircleIcon,
  Facebook,
  Linkedin,
} from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { contactData } from "../data/contactData";
import ReCAPTCHA from "react-google-recaptcha";
import Image from "next/image";

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  pincode: string;
  city: string;
  bill: string;
  message: string;
}

const Contact = () => {
  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    email: "",
    phone: "",
    pincode: "",
    city: "",
    bill: "",
    message: "",
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [loading, setLoading] = useState(false); // Track loading state
  const [success, setSuccess] = useState(false); // Track success state
  const [showCaptchaError, setShowCaptchaError] = useState(false); // Track reCAPTCHA error
  const recaptchaRef = useRef<ReCAPTCHA>(null); // Ref for reCAPTCHA
  const [showCaptcha, setShowCaptcha] = useState(false);

  const validate = () => {
    let tempErrors: Partial<FormData> = {};
    if (!formData.fullName) tempErrors.fullName = "Name is required";
    if (!formData.email) {
      tempErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      tempErrors.email = "Invalid email format";
    }
    if (!formData.phone) {
      tempErrors.phone = "Phone number is required";
    } else if (!/^[0-9]{10}$/.test(formData.phone)) {
      tempErrors.phone = "Invalid phone number (10 digits required)";
    }
    if (!formData.bill) tempErrors.bill = "Please select monthly Bill";
    if (!formData.message) tempErrors.message = "Message is required";
    if (!formData.pincode) tempErrors.pincode = "Please enter Pincode";
    if (!formData.city) tempErrors.city = "Please enter City";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    // Validate form fields
    if (!validate()) return;

    // Validate reCAPTCHA
    const recaptchaValue = recaptchaRef.current?.getValue();
    if (!recaptchaValue) {
      setShowCaptchaError(true);
      return;
    }

    setLoading(true); // Start loading
    setShowCaptchaError(false); // Reset reCAPTCHA error

    try {
      const body = `Sender's Message-\n\nName : ${formData.fullName}\nWhatsapp No.: ${formData.phone}\nEmail : ${formData.email}\nBill : ${formData.bill} \nPincode : ${formData.pincode}\nCity : ${formData.city}\nMessage : ${formData.message}`;
      console.log("body ==========", body);
      const response = await fetch("/api/sendEmail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ body }),
      });
      console.log("response", response);

      if (response.ok) {
        setSuccess(true); // Show success message
        setTimeout(() => setSuccess(false), 2000); // Hide success message after 2 seconds

        // Reset form data
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          pincode: "",
          city: "",
          bill: "",
          message: "",
        });

        // Reset reCAPTCHA
        recaptchaRef.current?.reset();
      }
    } catch (error) {
      console.log("error in mail sending ", error);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  useEffect(() => {
    const handleInteraction = () => setShowCaptcha(true);
    document.addEventListener("focus", handleInteraction, true);
    return () => document.removeEventListener("focus", handleInteraction, true);
  }, []);

  return (
    <div className="flex flex-col bg-quad lg:justify-evenly py-[4rem] px-[1.5rem] sm:px-[3rem] lg:px-[6rem]">
      <div className="flex flex-col gap-3 mb-4">
        <div className="text-pri text-desc-size2 flex items-center gap-2">
          <Sun size={20} />
          <span>CONTACT</span>
        </div>
        <div className="text-text1 text-title-size3 lg:text-title-size2 md:w-[60vw] leading-[1]">
          {contactData.title}
        </div>
      </div>

      <div className="flex flex-col-reverse md:flex-row w-full shadow-sm rounded-xl bg-hex p-2 sm:p-4 max-w-screen-2xl self-center">
        {/* sec1 */}
        <div className="sec1 flex flex-col text-color4 rounded-lg bg-pri md:w-5/12 p-4 sm:p-6 md:p-3 lg:p-6 justify-between gap-16 md:gap-0">
          {/* Content of sec1 */}
          <div className="flex flex-col gap-2">
            <div className="text-desc-size1 sm:text-title-size3 font-weight2 leading-[1]">
              Contact Information
            </div>
            <div className="text-desc-size2 font-weight3">
              Say something to start a live chat!
            </div>
          </div>

          <div className="flex flex-col gap-2 md:gap-2">
            <div className="flex flex-row gap-2 items-center">
              <Phone size={20} className="text-color4" />
              <div className="text-desc-size2">
                {contactData.contactInfo.phone}
              </div>
            </div>

            <div className="flex flex-row gap-2 items-center">
              <Mail size={20} className="text-color4" />
              <div className="text-desc-size2">
                {contactData.contactInfo.email}
              </div>
            </div>

            <div className="flex flex-row gap-2 items-center">
              <MapPin size={20} className="text-color4" />
              <div className="text-desc-size2">
                {contactData.contactInfo.address}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="relative flex h-[160px] bg-white rounded-md overflow-hidden border-2 border-white">
              <iframe
                title="Google Map"
                className="w-full h-full"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1148.643439293102!2d79.04402667164035!3d21.11433204795647!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2866b01bfd6b1517%3A0x6ac584a87296c3f5!2sSatyanam%20Solar!5e0!3m2!1sen!2sin!4v1749027611289!5m2!1sen!2sin"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
            <div className="flex flex-row gap-4">
              {contactData.socialMedia.map((social, index) => (
                <Link
                  key={index}
                  rel="noopener noreferrer"
                  href={social.link}
                  target="_blank"
                  aria-label={social.icon}
                  className="w-8 h-8 rounded-full bg-hex bg-opacity-60 flex items-center justify-center"
                >
                  {social.icon === "Instagram" && (
                    <Image
                      src="/home/ig.png"
                      width={20}
                      height={20}
                      alt="Instagram"
                    />
                    // <Instagram size={20} className="text-pri" />
                  )}
                  {social.icon === "Facebook" && (
                    <Image
                      src="/home/fb.png"
                      width={22}
                      height={22}
                      alt="Facebook"
                    />
                    // <Facebook size={20} className="text-pri" />
                  )}
                  {social.icon === "LinkedIn" && (
                    <Image
                      src="/home/ln.png"
                      width={20}
                      height={20}
                      alt="LinkedIn"
                    />
                    // <Linkedin size={20} className="text-pri" />
                  )}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* sec2 */}
        <div className="sec2 flex flex-col gap-4 md:w-7/12 p-1 py-8 md:py-1 sm:px-4 lg:px-12 xl:px-16 text-color3 text-desc-size2">
          <form
            onSubmit={handleSubmit}
            className="flex flex-col justify-between gap-6 md:items-end h-full"
          >
            {/* Form fields */}
            <div className="flex flex-col gap-6">
              {/* Full Name */}
              <div className="flex flex-col sm:flex-row">
                <div className="w-full">
                  <label className="text-desc-size2 font-weight3 text-text1">
                    Full Name
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className="w-full p-[1px] border-b border-gray-300 focus:outline-none focus:border-pri bg-transparent"
                      aria-labelledby="fullName"
                    />
                  </label>
                  {errors.fullName && (
                    <p className="text-red-500 text-sm">{errors.fullName}</p>
                  )}
                </div>
              </div>
              {/* Phone and Email */}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-12">
                <div className="w-full sm:w-1/2">
                  <label className="text-desc-size2 font-weight3 text-text1">
                    Whatsapp Number
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full p-[1px] border-b border-gray-300 focus:outline-none focus:border-pri bg-transparent"
                      aria-labelledby="phone"
                    />
                  </label>
                  {errors.phone && (
                    <p className="text-red-500 text-sm">{errors.phone}</p>
                  )}
                </div>
                <div className="w-full sm:w-1/2">
                  <label className="text-desc-size2 font-weight3 text-text1">
                    Email
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full p-[1px] border-b border-gray-300 focus:outline-none focus:border-pri bg-transparent"
                      aria-labelledby="email"
                    />
                  </label>
                  {errors.email && (
                    <p className="text-red-500 text-sm">{errors.email}</p>
                  )}
                </div>
              </div>
              {/* Monthly Bill */}
              <div className="flex flex-col gap-2">
                <label className="text-desc-size2 font-weight3 text-text1">
                  Monthly Electricity Bill
                </label>
                <div className="flex flex-wrap gap-1">
                  {[
                    "Less Than 1500",
                    "Rs.1500-Rs.2500",
                    "Rs.2500-Rs.4000",
                    "Rs.4000-Rs.8000",
                    "More than Rs.4000",
                  ].map((bill) => (
                    <label
                      key={bill}
                      className="flex items-center gap-1 mr-3 text-sm"
                    >
                      <input
                        type="radio"
                        name="bill"
                        value={bill}
                        checked={formData.bill === bill}
                        onChange={handleChange}
                        className="accent-pri"
                      />
                      <span className="text-desc text-color3">{bill}</span>
                    </label>
                  ))}
                </div>
                {errors.bill && (
                  <p className="text-red-500 text-sm">{errors.bill}</p>
                )}
              </div>
              {/* Pincode and City */}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-12">
                <div className="w-full sm:w-1/2">
                  <label className="text-desc-size2 font-weight3 text-text1">
                    Pincode
                    <input
                      type="tel"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      className="w-full p-[1px] border-b border-gray-300 focus:outline-none focus:border-pri bg-transparent"
                      aria-labelledby="pincode"
                    />
                  </label>
                  {errors.pincode && (
                    <p className="text-red-500 text-sm">{errors.pincode}</p>
                  )}
                </div>
                <div className="w-full sm:w-1/2">
                  <label className="text-desc-size2 font-weight3 text-text1">
                    City
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full p-[1px] border-b border-gray-300 focus:outline-none focus:border-pri bg-transparent"
                      aria-labelledby="city"
                    />
                  </label>
                  {errors.city && (
                    <p className="text-red-500 text-sm">{errors.city}</p>
                  )}
                </div>
              </div>
              {/* Message */}
              <div className="flex flex-col gap-2">
                <label className="text-desc-size2 font-weight3 text-text1">
                  Message
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full p-2 border-b border-gray-300 focus:outline-none focus:border-pri bg-transparent resize-none"
                    rows={1}
                    placeholder="Write Your Message..."
                  />
                </label>
                {errors.message && (
                  <p className="text-red-500 text-sm">{errors.message}</p>
                )}
              </div>
              {/* reCAPTCHA */}
              <div className="recaptcha-wrapper">
                {" "}
                {/* Reduced from 4 levels to 2 */}
                {/* {showCaptcha && ( */}
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LdNWfoqAAAAAOgnYz9XkXxeMRbKYpL7I_LXILdW"
                    className="mx-auto transform scale-75 -mt-6"
                  />
                {/* )} */}
                {showCaptchaError && (
                  <p className="text-red-500 text-sm text-center -mt-6">
                    Please verify that you are not a robot.
                  </p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="bg-pri text-color4 px-6 py-2 rounded-md hover:bg-pri-dark transition-colors hover:scale-105 translate-x-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </div>

            {/* Success Message */}
            {success && (
              <div className="text-green-600 -mt-6">
                Message sent successfully!
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
